import { useEffect, useState } from "react";
import axios from "axios";
import {
  FaCheckCircle,
  FaMapMarkerAlt,
  FaRoute,
  FaSyncAlt,
  FaTruck,
} from "react-icons/fa";
import {
  MapContainer,
  Marker,
  Polyline,
  Popup,
  TileLayer,
  useMap,
} from "react-leaflet";
import L from "leaflet";

import "leaflet/dist/leaflet.css";
import "./Flotta.css";
import ViaggioFiorinoCard from "./ViaggioFiorinoCard";
import StoricoViaggiFlotta from "./StoricoViaggiFlotta";
import FlottaObd from "./FlottaObd";
import FlottaSalute from "./FlottaSalute";
// BETALUMINA_FLOTTA_RIFORNIMENTI_V2A_20260901
import FlottaRifornimenti from "./FlottaRifornimenti";
import PianificatorePercorsi from "./PianificatorePercorsi";

const API_BASE_URL =
  process.env.REACT_APP_API_URL ||
  "https://interventi.synology.me:5050";

delete L.Icon.Default.prototype._getIconUrl;

L.Icon.Default.mergeOptions({
  iconRetinaUrl: require(
    "leaflet/dist/images/marker-icon-2x.png"
  ),
  iconUrl: require(
    "leaflet/dist/images/marker-icon.png"
  ),
  shadowUrl: require(
    "leaflet/dist/images/marker-shadow.png"
  ),
});

const destinationIcon = L.divIcon({
  className: "flotta-destination-icon",
  html: "<div>📍</div>",
  iconSize: [34, 34],
  iconAnchor: [17, 31],
  popupAnchor: [0, -30],
});

const hasCoordinates = (item) =>
  Number.isFinite(Number(item?.latitude)) &&
  Number.isFinite(Number(item?.longitude));

const formatVoltage = (value) => {
  const number = Number(value);

  return Number.isFinite(number)
    ? `${number.toFixed(2)} V`
    : "—";
};

const formatDistance = (value) => {
  const number = Number(value);

  return Number.isFinite(number)
    ? `${number.toFixed(1)} km`
    : "—";
};

const formatDate = (value) => {
  if (!value) return "—";

  const text = String(value);

  if (/^\d{4}-\d{2}-\d{2}/.test(text)) {
    const [year, month, day] = text
      .slice(0, 10)
      .split("-");

    return `${day}/${month}/${year}`;
  }

  const date = new Date(value);

  return Number.isNaN(date.getTime())
    ? "—"
    : date.toLocaleDateString("it-IT");
};

const AggiornaInquadratura = ({
  position,
  destination,
}) => {
  const map = useMap();

  useEffect(() => {
    const points = [];

    if (hasCoordinates(position)) {
      points.push([
        Number(position.latitude),
        Number(position.longitude),
      ]);
    }

    if (hasCoordinates(destination)) {
      points.push([
        Number(destination.latitude),
        Number(destination.longitude),
      ]);
    }

    if (points.length === 2) {
      map.fitBounds(points, {
        padding: [45, 45],
        maxZoom: 16,
      });

      return;
    }

    if (points.length === 1) {
      map.setView(points[0], 16);
    }
  }, [
    map,
    position?.latitude,
    position?.longitude,
    destination?.latitude,
    destination?.longitude,
  ]);

  return null;
};

const Flotta = ({
  onApriIntervento,
}) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  // === BETALUMINA FLOTTA SECTION PERSISTENCE V1 - 2026-08-30 ===
  const [sezioneFlotta, setSezioneFlotta] =
    useState(() => {
      const stored = String(
        sessionStorage.getItem(
          "betalumina:flotta:sezione:v1"
        ) || ""
      ).trim();

      const allowed = [
        "attivo",
        "storico",
        "obd",
        "pianifica",
        "salute",
        "rifornimenti",
      ];

      return allowed.includes(stored)
        ? stored
        : "attivo";
    });

  useEffect(() => {
    sessionStorage.setItem(
      "betalumina:flotta:sezione:v1",
      sezioneFlotta
    );
  }, [sezioneFlotta]);
  // === END BETALUMINA FLOTTA SECTION PERSISTENCE V1 ===

  const caricaFlotta = async ({
    mostraCaricamento = false,
  } = {}) => {
    if (mostraCaricamento) {
      setLoading(true);
    }

    try {
      const token = localStorage.getItem("token");

      const response = await axios.get(
        `${API_BASE_URL}/api/flotta/dashboard`,
        {
          headers: token
            ? {
                Authorization: `Bearer ${token}`,
              }
            : {},
        }
      );

      setData(response.data);
      setError("");
    } catch (err) {
      console.error(
        "Errore caricamento dashboard flotta:",
        err
      );

      setError(
        "Impossibile caricare i dati della flotta"
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    caricaFlotta({
      mostraCaricamento: true,
    });

    const intervallo = setInterval(
      () => caricaFlotta(),
      10000
    );

    return () => clearInterval(intervallo);
  }, []);

  if (loading && !data) {
    return <p>Caricamento dati flotta...</p>;
  }

  if (error && !data) {
    return (
      <div className="flotta-error">
        <p>{error}</p>

        <button
          type="button"
          onClick={() =>
            caricaFlotta({
              mostraCaricamento: true,
            })
          }
        >
          <FaSyncAlt /> Riprova
        </button>
      </div>
    );
  }

  const vehicle = data?.vehicle;
  const position = vehicle?.position;

  // === 1NCE FLOTTA READ-ONLY UI V4 - 2026-08-12 ===
  const oneNce = data?.oneNce;
  const sim = oneNce?.sim;

  // === 1NCE DIAGNOSTICA UI V10 - 2026-08-12 ===
  const oneNceDiagnostics = data?.oneNceDiagnostics;
  const network = oneNceDiagnostics?.network;
  const dataQuota = oneNceDiagnostics?.dataQuota;
  const smsQuota = oneNceDiagnostics?.smsQuota;
  const simEvents = Array.isArray(oneNceDiagnostics?.events)
    ? oneNceDiagnostics.events
    : [];

  const nextIntervention =
    data?.nextIntervention;

  const destination =
    nextIntervention?.destination;

  const routePositions = Array.isArray(
    nextIntervention?.routeGeometry
  )
    ? nextIntervention.routeGeometry
        .filter(
          (point) =>
            Array.isArray(point) &&
            point.length >= 2 &&
            Number.isFinite(Number(point[0])) &&
            Number.isFinite(Number(point[1]))
        )
        .map((point) => [
          Number(point[1]),
          Number(point[0]),
        ])
    : [];

  const statoOnline =
    vehicle?.status === "online";

  const mapCenter = hasCoordinates(position)
    ? [
        Number(position.latitude),
        Number(position.longitude),
      ]
    : hasCoordinates(destination)
    ? [
        Number(destination.latitude),
        Number(destination.longitude),
      ]
    : null;

  const apriPosizioneFiorino = () => {
    if (!hasCoordinates(position)) return;

    window.open(
      `https://www.google.com/maps?q=${position.latitude},${position.longitude}`,
      "_blank",
      "noopener,noreferrer"
    );
  };

  const avviaNavigazione = () => {
    if (!hasCoordinates(destination)) return;

    const destinationCoords =
      `${destination.latitude},${destination.longitude}`;

    const origin = hasCoordinates(position)
      ? `&origin=${position.latitude},${position.longitude}`
      : "";

    window.open(
      `https://www.google.com/maps/dir/?api=1${origin}&destination=${destinationCoords}&travelmode=driving`,
      "_blank",
      "noopener,noreferrer"
    );
  };

  return (
    <div className="flotta-container">
      <div className="flotta-title-row">
        <h2 className="flotta-title">
          <FaTruck /> Flotta
        </h2>

        <button
          type="button"
          className="flotta-refresh-button"
          onClick={() => caricaFlotta()}
        >
          <FaSyncAlt /> Aggiorna
        </button>
      </div>

      {error && (
        <div className="flotta-warning">
          {error}. Sono mostrati gli ultimi dati
          disponibili.
        </div>
      )}

      <div className="flotta-subtabs">
        <button
          type="button"
          className={`flotta-subtab ${
            sezioneFlotta === "attivo"
              ? "active"
              : ""
          }`}
          onClick={() =>
            setSezioneFlotta("attivo")
          }
        >
          Viaggio attivo
        </button>

        <button
          type="button"
          className={`flotta-subtab ${
            sezioneFlotta === "storico"
              ? "active"
              : ""
          }`}
          onClick={() =>
            setSezioneFlotta("storico")
          }
        >
          Storico viaggi
        </button>

        <button
          type="button"
          className={`flotta-subtab ${
            sezioneFlotta === "obd"
              ? "active"
              : ""
          }`}
          onClick={() =>
            setSezioneFlotta("obd")
          }
        >
          Diagnostica OBD
        </button>

        <button
          type="button"
          className={`flotta-subtab ${
            sezioneFlotta === "pianifica"
              ? "active"
              : ""
          }`}
          onClick={() =>
            setSezioneFlotta("pianifica")
          }
        >
          Pianificatore
        </button>

        <button
          type="button"
          className={`flotta-subtab ${
            sezioneFlotta === "salute"
              ? "active"
              : ""
          }`}
          onClick={() =>
            setSezioneFlotta("salute")
          }
        >
          Salute veicolo
        </button>
      
        <button
          type="button"
          className={`flotta-subtab ${
            sezioneFlotta === "rifornimenti"
              ? "active"
              : ""
          }`}
          onClick={() =>
            setSezioneFlotta("rifornimenti")
          }
        >
          Rifornimenti
        </button>
</div>

      {sezioneFlotta === "attivo" ? (
        <>
          <ViaggioFiorinoCard />

          <div className="flotta-layout">
        <section className="flotta-card">
          <div className="flotta-header">
            <div>
              <h3>{vehicle?.name || "Fiorino"}</h3>

              <p>
                {vehicle?.model ||
                  "Teltonika FMC920"}
              </p>
            </div>

            <span
              className={`flotta-status ${
                statoOnline
                  ? "online"
                  : "offline"
              }`}
            >
              {statoOnline
                ? "Online"
                : "Offline"}
            </span>
          </div>

          <div className="flotta-grid">
            <div>
              <strong>Accensione</strong>
              <span>
                {position?.ignition
                  ? "ON"
                  : "OFF"}
              </span>
            </div>

            <div>
              <strong>Movimento</strong>
              <span>
                {position?.motion
                  ? "Sì"
                  : "No"}
              </span>
            </div>

            <div>
              <strong>Velocità</strong>
              <span>
                {position?.speedKmh ?? 0} km/h
              </span>
            </div>

            <div>
              <strong>Alimentazione</strong>
              <span>
                {formatVoltage(position?.power)}
              </span>
            </div>

            <div>
              <strong>Batteria tracker</strong>
              <span>
                {formatVoltage(
                  position?.battery
                )}
              </span>
            </div>

            <div>
              <strong>Satelliti</strong>
              <span>
                {position?.satellites ?? "—"}
              </span>
            </div>
          </div>

          <div className="flotta-address">
            <strong>Posizione attuale</strong>
            <p>
              {position?.address ||
                "Indirizzo non disponibile"}
            </p>
          </div>

                      <div className="flotta-sim-panel">
              <div className="flotta-sim-heading">
                <div>
                  <strong>SIM 1NCE</strong>
                  <span>Management API v2 · sola lettura</span>
                </div>

                <span
                  className={`flotta-sim-badge ${
                    oneNce?.apiOk &&
                    String(sim?.status || "")
                      .toLowerCase() === "enabled"
                      ? "enabled"
                      : oneNce?.apiOk
                        ? "available"
                        : "error"
                  }`}
                >
                  {oneNce?.apiOk
                    ? sim?.status || "API OK"
                    : "API non disponibile"}
                </span>
              </div>

              {oneNce?.apiOk && sim ? (
                <div className="flotta-sim-grid">
                  <div>
                    <strong>ICCID</strong>
                    <span>{sim.iccid || "—"}</span>
                  </div>

                  <div>
                    <strong>IMSI</strong>
                    <span>{sim.imsi || "—"}</span>
                  </div>

                  <div>
                    <strong>MSISDN</strong>
                    <span>{sim.msisdn || "—"}</span>
                  </div>

                  <div>
                    <strong>IP SIM</strong>
                    <span>{sim.ip || "—"}</span>
                  </div>

                  <div>
                    <strong>IMEI visto da 1NCE</strong>
                    <span>{sim.imei || "—"}</span>
                  </div>

                  <div>
                    <strong>Ultimo controllo API</strong>
                    <span>
                      {oneNce?.fetchedAt
                        ? new Date(
                            oneNce.fetchedAt
                          ).toLocaleString("it-IT")
                        : "—"}
                    </span>
                  </div>
                </div>
              ) : (
                <p className="flotta-sim-error">
                  {oneNce?.error ||
                    "Dati SIM 1NCE non disponibili"}
                </p>
              )}
            </div>

            <div className="flotta-sim-diagnostics">
              <div className="flotta-diagnostics-heading">
                <div>
                  <strong>Diagnostica rete 1NCE</strong>
                  <span>Status · quote · eventi</span>
                </div>

                <span
                  className={`flotta-network-badge ${
                    String(network?.status || "").toLowerCase()
                  }`}
                >
                  {network?.status ||
                    (oneNceDiagnostics?.apiOk ? "API OK" : "n/d")}
                </span>
              </div>

              {oneNceDiagnostics?.apiOk ? (
                <>
                  <div className="flotta-network-grid">
                    <div><strong>Operatore</strong><span>{network?.operator || "—"}</span></div>
                    <div>
                      <strong>Paese</strong>
                      <span>
                        {network?.country || "—"}
                        {network?.countryIso ? ` (${String(network.countryIso).toUpperCase()})` : ""}
                      </span>
                    </div>
                    <div><strong>Tecnologia rete</strong><span>{network?.rat || "—"}</span></div>
                    <div>
                      <strong>Ultimo aggiornamento GPRS</strong>
                      <span>
                        {network?.lastUpdatedGprs
                          ? new Date(network.lastUpdatedGprs).toLocaleString("it-IT")
                          : "—"}
                      </span>
                    </div>
                    <div>
                      <strong>PDP context</strong>
                      <span>
                        {network?.pdpDuration
                          ? `attivo da ${network.pdpDuration}`
                          : network?.pdpCreated
                            ? new Date(network.pdpCreated).toLocaleString("it-IT")
                            : "—"}
                      </span>
                    </div>
                    <div><strong>IMEISV rete</strong><span>{network?.imeisv || "—"}</span></div>
                  </div>

                  <div className="flotta-quota-grid">
                    <div className="flotta-quota-card">
                      <div className="flotta-quota-title">
                        <strong>Dati</strong>
                        <span>{dataQuota ? `${Number(dataQuota.remaining).toFixed(2)} MB residui` : "—"}</span>
                      </div>
                      <div className="flotta-quota-bar">
                        <span style={{ width: `${Math.max(0, Math.min(100, Number(dataQuota?.remainingPercent || 0)))}%` }} />
                      </div>
                      <small>
                        {dataQuota
                          ? `${Number(dataQuota.used).toFixed(2)} MB usati su ${Number(dataQuota.total).toFixed(2)} MB`
                          : "Quota non disponibile"}
                      </small>
                    </div>

                    <div className="flotta-quota-card">
                      <div className="flotta-quota-title">
                        <strong>SMS</strong>
                        <span>{smsQuota ? `${Number(smsQuota.remaining).toFixed(0)} residui` : "—"}</span>
                      </div>
                      <div className="flotta-quota-bar">
                        <span style={{ width: `${Math.max(0, Math.min(100, Number(smsQuota?.remainingPercent || 0)))}%` }} />
                      </div>
                      <small>
                        {smsQuota
                          ? `${Number(smsQuota.used).toFixed(0)} usati su ${Number(smsQuota.total).toFixed(0)}`
                          : "Quota non disponibile"}
                      </small>
                    </div>
                  </div>

                  {dataQuota?.expiryDate && (
                    <div className="flotta-quota-expiry">
                      Scadenza quota: {new Date(dataQuota.expiryDate).toLocaleDateString("it-IT")}
                    </div>
                  )}

                  <div className="flotta-events-block">
                    <strong>Ultimi eventi 1NCE</strong>
                    {simEvents.length ? (
                      <div className="flotta-events-list">
                        {simEvents.map((event, index) => (
                          <div
                            className={`flotta-event-row ${event?.alert ? "alert" : ""}`}
                            key={`${event?.timestamp || "event"}-${index}`}
                          >
                            <div>
                              <span className="flotta-event-type">{event?.eventType || "Evento 1NCE"}</span>
                              <small>{[event?.operator, event?.country].filter(Boolean).join(" · ") || "—"}</small>
                            </div>
                            <div className="flotta-event-meta">
                              <span>{event?.severity || "Info"}</span>
                              <small>{event?.timestamp ? new Date(event.timestamp).toLocaleString("it-IT") : "—"}</small>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="flotta-events-empty">Nessun evento recente.</p>
                    )}
                  </div>
                </>
              ) : (
                <p className="flotta-sim-error">
                  {oneNceDiagnostics?.error || "Diagnostica 1NCE non disponibile"}
                </p>
              )}
            </div>

<div className="flotta-footer">
            <div>
              <strong>
                Ultimo aggiornamento
              </strong>

              <p>
                {vehicle?.lastUpdate
                  ? new Date(
                      vehicle.lastUpdate
                    ).toLocaleString("it-IT")
                  : "—"}
              </p>
            </div>

            <button
              type="button"
              onClick={apriPosizioneFiorino}
              disabled={
                !hasCoordinates(position)
              }
            >
              <FaMapMarkerAlt />
              Posizione Fiorino
            </button>
          </div>
        </section>

        <section className="flotta-card flotta-next-card">
          <h3>
            <FaRoute /> Prossimo intervento
          </h3>

          {!nextIntervention ? (
            <div className="flotta-empty">
              Nessun intervento “Da fare” con
              indirizzo disponibile.
            </div>
          ) : (
            <>
              <div className="flotta-next-header">
                <div>
                  <strong className="flotta-next-client">
                    {nextIntervention.cliente ||
                      "Cliente non indicato"}
                  </strong>

                  <p>
                    {nextIntervention.causale ||
                      nextIntervention.descrizione ||
                      "Intervento senza causale"}
                  </p>
                </div>

                {nextIntervention.arrived && (
                  <span className="flotta-arrived">
                    <FaCheckCircle /> Arrivato
                  </span>
                )}
              </div>

              <div className="flotta-next-grid">
                <div>
                  <strong>Indirizzo</strong>
                  <span>
                    {nextIntervention.indirizzo ||
                      "—"}
                  </span>
                </div>

                <div>
                  <strong>Data</strong>
                  <span>
                    {formatDate(
                      nextIntervention.data
                    )}
                  </span>
                </div>

                <div>
                  <strong>Ora</strong>
                  <span>
                    {nextIntervention.oraInizio ||
                      "—"}
                  </span>
                </div>

                <div>
                  <strong>
                    {nextIntervention.routeSource === "osrm"
                      ? "Distanza stradale"
                      : "Distanza indicativa"}
                  </strong>
                  <span>
                    {formatDistance(
                      nextIntervention.distanceKm
                    )}
                  </span>
                </div>

                <div>
                  <strong>Tempo stimato</strong>
                  <span>
                    {Number.isFinite(
                      Number(
                        nextIntervention.durationMinutes
                      )
                    )
                      ? `${nextIntervention.durationMinutes} min`
                      : "—"}
                  </span>
                </div>

                <div>
                  <strong>Calcolo percorso</strong>
                  <span>
                    {nextIntervention.routeSource === "osrm"
                      ? "Percorso stradale"
                      : "Linea d’aria"}
                  </span>
                </div>

                <div>
                  <strong>Geofence</strong>
                  <span>
                    {nextIntervention.arrived
                      ? "Fiorino nella zona"
                      : `${nextIntervention.geofenceRadiusMeters || 150} m`}
                  </span>
                </div>

                <div>
                  <strong>Stato</strong>
                  <span>
                    {nextIntervention.stato ||
                      "Da fare"}
                  </span>
                </div>
              </div>

              {!destination && (
                <div className="flotta-geocode-warning">
                  L’indirizzo non è stato
                  riconosciuto dal geocoder.
                  Inserire via, numero civico,
                  città e provincia.
                </div>
              )}

              <button
                type="button"
                className="flotta-navigation-button"
                onClick={() =>
                  onApriIntervento?.(
                    nextIntervention
                  )
                }
              >
                Apri intervento #{nextIntervention.id}
              </button>

              <button
                type="button"
                className="flotta-navigation-button"
                onClick={avviaNavigazione}
                disabled={
                  !hasCoordinates(destination)
                }
              >
                <FaRoute />
                Avvia navigazione
              </button>
            </>
          )}
        </section>
      </div>

      {mapCenter && (
        <div className="flotta-map-wrapper">
          <MapContainer
            center={mapCenter}
            zoom={16}
            scrollWheelZoom
            className="flotta-map"
          >
            <AggiornaInquadratura
              position={position}
              destination={destination}
            />

            <TileLayer
              attribution="&copy; OpenStreetMap contributors"
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            />

            {routePositions.length > 1 && (
              <Polyline
                positions={routePositions}
                pathOptions={{
                  weight: 5,
                  opacity: 0.75,
                }}
              />
            )}

            {hasCoordinates(position) && (
              <Marker
                position={[
                  Number(position.latitude),
                  Number(position.longitude),
                ]}
              >
                <Popup>
                  <strong>
                    {vehicle?.name || "Fiorino"}
                  </strong>
                  <br />
                  Velocità:{" "}
                  {position?.speedKmh ?? 0} km/h
                  <br />
                  Accensione:{" "}
                  {position?.ignition
                    ? "ON"
                    : "OFF"}
                </Popup>
              </Marker>
            )}

            {hasCoordinates(destination) && (
              <Marker
                icon={destinationIcon}
                position={[
                  Number(destination.latitude),
                  Number(destination.longitude),
                ]}
              >
                <Popup>
                  <strong>Destinazione</strong>
                  <br />
                  {nextIntervention?.cliente}
                  <br />
                  {nextIntervention?.indirizzo}
                </Popup>
              </Marker>
            )}
          </MapContainer>
        </div>
      )}
        </>
      ) : sezioneFlotta === "storico" ? (
        <StoricoViaggiFlotta
          onApriIntervento={onApriIntervento}
        />
      ) : sezioneFlotta === "obd" ? (
        <FlottaObd />
      ) : sezioneFlotta === "pianifica" ? (
        <PianificatorePercorsi />
      ) : sezioneFlotta === "rifornimenti" ? (
        <FlottaRifornimenti />
      ) : (
        <FlottaSalute />
      )}
    </div>
  );
};

export default Flotta;
